// @ts-nocheck

/* eslint-disable camelcase */

import _ from 'lodash';

/**
 * Mean amplitude of a sample array, used by the Pan-Tompkins threshold estimates. Divides the lodash sum by the array length.
 *
 * @param array - Samples to average.
 * @returns Arithmetic mean of the entries.
 */
export const getAverage = function (array: any) {
  return _.sum(array) / array.length;
};

/**
 * Slices a window of samples from an array starting at an index without running past its end. Falls back to slicing through to the array end when the requested count overruns it.
 *
 * @param array - Array to slice.
 * @param startIdx - First index of the window.
 * @param count - Desired window length.
 * @returns The requested sample window.
 */
export const getRange = function (array: any, startIdx: any, count: any) {
  let result = [];
  if (startIdx + count > array.length) {
    result = _.slice(array, startIdx, array.length);
  } else {
    result = _.slice(array, startIdx, startIdx + count);
  }
  return result;
};

/**
 * Appends every element of one array onto another in place. Iterates with forEach and push rather than spreading, which fails in Safari for large arrays.
 *
 * @param arrayDes - Array appended to, mutated in place.
 * @param arrayIn - Array whose elements are copied over.
 * @returns Nothing.
 */
export const addRange = function (arrayDes: any, arrayIn: any) {
  // arrayDes.push(...arrayIn); // *: This method cause error in Safari
  arrayIn.forEach((e: any) => arrayDes.push(e));
};

/**
 * Builds a consecutive integer sequence used to index ECG sample windows. Logs and returns null for a negative count or a range beyond the numeric limit, otherwise pushes count values starting at start.
 *
 * @param start - First integer of the sequence.
 * @param count - How many integers to generate.
 * @returns The generated integers, or null for invalid input.
 */
export const createRange = function (start: any, count: any) {
  if (start + count - 1 > Infinity) {
    console.log('Exception at createRange: out of range');
    return null;
  }
  if (count >= 0) {
    const result = [];
    for (let i = start; i < start + count; i += 1) {
      result.push(i);
    }
    return result;
  }
  console.log('Exception at createRange: unexpected second parameter');
  return null;
};

/**
 * Copies a run of elements from one array into another at a given offset. Logs an out-of-range message when either side is too short and still copies element by element.
 *
 * @param source - Array read from.
 * @param sourcePos - First index read in the source.
 * @param des - Array written to, mutated in place.
 * @param desPos - First index written in the destination.
 * @param count - Number of elements to copy.
 * @returns Nothing.
 */
export const copyArray = function (source: any, sourcePos: any, des: any, desPos: any, count: any) {
  if (des.length < desPos + count || source.length < sourcePos + count) {
    console.log('Error! Unexpected input: out of range');
  }
  let j = sourcePos;
  for (let i = desPos; i < desPos + count; i += 1) {
    des[i] = source[j];
    j += 1;
  }
};

/**
 * Band-passes an ECG trace with a tenth-order Butterworth IIR stage as the Pan-Tompkins pre-processing step. Runs the fixed coefficient difference equation forward over the samples with GAIN-scaled shift registers, then re-runs it backwards over the intermediate result for zero-phase filtering.
 *
 * @param data - ECG samples to filter.
 * @returns The forward-backward filtered samples.
 */
export const Butterworth_bandpass = function (data: any) {
  const dataLength = data.length;
  const filt_data = new Array(dataLength);
  const Nzeros = 10;
  const Npoles = 10;
  const GAIN = 4.673735821e4;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);
  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] =
      xv[10] -
      xv[0] +
      5 * (xv[2] - xv[8]) +
      10 * (xv[6] - xv[4]) +
      -0.4422091824 * yv[0] +
      4.6680654686 * yv[1] +
      -22.31324764 * yv[2] +
      63.59414794 * yv[3] +
      -119.67119788 * yv[4] +
      155.35936094 * yv[5] +
      -140.91060266 * yv[6] +
      88.167268075 * yv[7] +
      -36.42097566 * yv[8] +
      8.9693904453 * yv[9];
    filt_data[i] = yv[10];
  }
  xv.fill(0);
  yv.fill(0);
  const back_filt_data = new Array(dataLength);
  for (let i = filt_data.length - 1; i > -1; i -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = filt_data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] =
      xv[10] -
      xv[0] +
      5 * (xv[2] - xv[8]) +
      10 * (xv[6] - xv[4]) +
      -0.4422091824 * yv[0] +
      4.6680654686 * yv[1] +
      -22.31324764 * yv[2] +
      63.59414794 * yv[3] +
      -119.67119788 * yv[4] +
      155.35936094 * yv[5] +
      -140.91060266 * yv[6] +
      88.167268075 * yv[7] +
      -36.42097566 * yv[8] +
      8.9693904453 * yv[9];
    back_filt_data[i] = yv[10];
  }
  return back_filt_data;
};

/**
 * Approximates the first derivative of an ECG trace over its first nx samples. Uses a forward difference at the first and last index and the average of the neighbouring differences in between.
 *
 * @param data - ECG samples.
 * @param nx - Number of samples to differentiate.
 * @returns Derivative value per sample.
 */
export const FirstDerivative = function (data: any, nx: any) {
  let i;
  let tmp1;
  let tmp2;
  const firstderv = new Array(nx);
  // *: Compute derivative first index
  firstderv[0] = data[1] - data[0];

  for (i = 1; i < nx - 2; i += 1) {
    tmp1 = data[i] - data[i - 1];
    tmp2 = data[i + 1] - data[i];
    firstderv[i] = (tmp2 + tmp1) / 2;
  }

  // *: Compute derivative last index
  firstderv[nx - 1] = data[nx - 1] - data[nx - 2];
  return firstderv;
};

/**
 * Squares every ECG sample to emphasise QRS energy in the Pan-Tompkins chain. Multiplies each entry by itself in place and hands the same array back.
 *
 * @param ecgArrIn - ECG samples, squared in place.
 * @returns The array holding the squared samples.
 */
export const Pow_list = function (ecgArrIn: any) {
  const ecgArr = ecgArrIn;
  const ecgArrLength = ecgArr.length;
  for (let i = 0; i < ecgArrLength; i += 1) {
    ecgArr[i] *= ecgArr[i];
  }
  return ecgArr;
};

/**
 * Pads an ECG trace symmetrically at both ends before filtering. Collects window_len reversed head samples and the matching tail samples, then appends the original trace and the tail block through addRange.
 *
 * @param ecg - ECG samples to pad.
 * @param window_len - Number of samples mirrored at each end.
 * @returns The padded sample list.
 */
export const Prepare_list = function (ecg: any, window_len: any) {
  const head_list = [];
  const tail_list = [];
  for (let i = parseInt(window_len, 10); i >= 0; i -= 1) {
    head_list.push(ecg[i]);
    tail_list.push(ecg[ecg.length - i]);
  }
  addRange(head_list, ecg);
  addRange(head_list, tail_list);
  //   ecg = head_list;
  return head_list;
};

/**
 * Convolves a signal with a kernel for the ECG peak detection chain. Offsets the kernel by half its length and keeps the output at the input length when trim is set, otherwise extends it, skipping taps that fall outside the signal.
 *
 * @param a - Signal samples.
 * @param kernel - Convolution kernel.
 * @param trim - Truthy to keep the result at the input length.
 * @returns The convolved samples.
 */
export const Convolve = function (a: any, kernel: any, trim: any) {
  let result;
  const m = parseInt(Math.ceil(kernel.length / 2.0) as any, 10);

  if (trim) {
    result = new Array(a.length);
    for (let i = 0; i < result.length; i += 1) {
      result[i] = 0;
      for (let j = 0; j < kernel.length; j += 1) {
        const k = i - j + m - 1;
        if (k >= 0 && k < a.length) {
          result[i] += a[k] * kernel[j];
        }
      }
    }
  } else {
    result = new Array(a.Length + m);
    for (let i = 0; i < result.length; i += 1) {
      result[i] = 0;
      for (let j = 0; j < kernel.length; j += 1) {
        const k = i - j;
        if (k >= 0 && k < a.length) {
          result[i] += a[k] * kernel[j];
        }
      }
    }
  }
  return result;
};

/**
 * Finds local maxima of a squared ECG signal that are at least a refractory distance apart. Accepts an index whose value equals the maximum of the surrounding window and then jumps ahead by the radius, handling the leading samples with a shorter window; the threshold argument is not applied.
 *
 * @param sig - Signal to scan for peaks.
 * @param threshold - Amplitude threshold, unused.
 * @param distance - Minimum spacing between peaks, in samples.
 * @returns Indices of the detected peaks.
 */
export const Find_peaks = function (sig: any, threshold: any, distance: any) {
  const peaks = [];
  let i = 0;
  const radius = parseInt(distance, 10);
  while (i < radius + 1) {
    if (sig[i] === _.max(getRange(sig, 0, i + radius))) {
      peaks.push(i);
      i += radius;
    } else {
      i += 1;
    }
  }
  while (i < sig.length) {
    if (
      sig[i] === _.max(getRange(sig, i - radius, _.min([radius * 2, sig.length - (i - radius)])))
    ) {
      peaks.push(i);
      i += radius;
    } else {
      i += 1;
    }
  }
  // while (i < sig.length) {
  //   if (sig[i] === _.max(getRange(sig, i - radius, sig.length - (i - radius)))) {
  //     peaks.push(i);
  //     i += radius;
  //   } else {
  //     i += 1;
  //   }
  // }
  return peaks;
};

/**
 * Reads the ECG amplitudes sitting at a list of beat locations. Pushes the sample at each integer-parsed location into a new array.
 *
 * @param ecg - ECG samples.
 * @param locs - Sample indices to read.
 * @returns Amplitudes at the requested locations.
 */
export const List_from_list = function (ecg: any, locs: any) {
  const new_list = [];
  for (let i = 0; i < locs.length; i += 1) {
    new_list.push(ecg[parseInt(locs[i], 10)]);
  }
  return new_list;
};

/**
 * Locates the largest amplitude inside an ECG window for the Pan-Tompkins search. Returns the maximum value, every index holding it and the first such index, falling back to zeros and an empty list for an empty window.
 *
 * @param ecg_array - ECG window to scan.
 * @returns Object with the maximum y_i, all matching indices i_l and the first index x_i.
 */
export const Find_max_locate = function (ecg_array: any): any {
  let y_i: any;
  let i_l: any;
  let x_i;
  if (ecg_array.length > 0) {
    y_i = _.max(ecg_array);
    i_l = _.range(ecg_array.length).filter((x): any => ecg_array[x] === y_i);
    x_i = ecg_array.findIndex((x: any): any => x === y_i);
  } else {
    y_i = 0;
    i_l = [];
    x_i = 0;
  }
  return { y_i, i_l, x_i };
};

/**
 * Computes the differences between consecutive samples, used for RR intervals and slope checks. Pushes each array[i + 1] minus array[i] into a new array one element shorter than the input.
 *
 * @param array - Samples to differentiate.
 * @returns Differences between neighbouring samples.
 */
export const Diff = function (array: any) {
  // return _.zipWith(array, _.slice(array, 1, array.length), (a, b) => (b - a));
  const newArr = [];
  for (let i = 0; i < array.length - 1; i += 1) {
    newArr.push(array[i + 1] - array[i]);
  }
  return newArr;
};

const compare_array = new Int16Array([
  -819, -813, -807, -801, -795, -788, -782, -776, -770, -764, -758, -752, -746, -740, -734, -729,
  -723, -717, -712, -706, -701, -695, -690, -685, -680, -675, -670, -665, -660, -656, -651, -647,
  -642, -638, -634, -630, -626, -622, -619, -615, -612, -608, -605, -602, -600, -597, -594, -592,
  -590, -587, -585, -584, -582, -580, -579, -578, -577, -576, -575, -574, -574, -574, -573, -573,
  -574, -574, -574, -575, -576, -577, -578, -579, -580, -582, -584, -585, -587, -590, -592, -594,
  -597, -600, -602, -605, -608, -612, -615, -619, -622, -626, -630, -634, -638, -642, -647, -651,
  -656, -660, -665, -670, -675, -680, -685, -690, -695, -701, -706, -712, -717, -723, -729, -734,
  -740, -746, -752, -758, -764, -770, -776, -782, -788, -795, -801, -807, -813, 819, 813, 807, 801,
  795, 788, 782, 776, 770, 764, 758, 752, 746, 740, 734, 729, 723, 717, 712, 706, 701, 695, 690,
  685, 680, 675, 670, 665, 660, 656, 651, 647, 642, 638, 634, 630, 626, 622, 619, 615, 612, 608,
  605, 602, 600, 597, 594, 592, 590, 587, 585, 584, 582, 580, 579, 578, 577, 576, 575, 574, 574,
  574, 573, 573, 574, 574, 574, 575, 576, 577, 578, 579, 580, 582, 584, 585, 587, 590, 592, 594,
  597, 600, 602, 605, 608, 612, 615, 619, 622, 626, 630, 634, 638, 642, 647, 651, 656, 660, 665,
  670, 675, 680, 685, 690, 695, 701, 706, 712, 717, 723, 729, 734, 740, 746, 752, 758, 764, 770,
  776, 782, 788, 795, 801, 807, 813,
]);

/**
 * Converts raw ECG counts into real amplitudes using the channel gain, yielding an empty array when the gain is zero. Divides each sample by the gain, substituting Number.MAX_VALUE for NaN results, and zeroes any run matching compare_array as a dropped-sample marker.
 *
 * @param ecg - Raw ECG counts.
 * @param gain - Channel gain; 0 produces an empty result.
 * @returns The scaled ECG values.
 */
export const Return_real_value = function (ecg: any, gain: any) {
  const ecg_output = [];
  if (gain !== 0) {
    let i_compare = 0;
    for (let i = 0; i < ecg.length; i += 1) {
      if (ecg[i] === compare_array[i_compare]) {
        i_compare += 1;
      } else {
        i_compare = 0;
      }

      if (!_.isNaN(ecg[i] / gain)) {
        ecg_output.push(ecg[i] / gain);
      } else {
        ecg_output.push(Number.MAX_VALUE);
      }

      if (i_compare === compare_array.length) {
        for (let j = 0; j < compare_array.length; j += 1) {
          ecg_output[i - j] = 0;
        }
        i_compare = 0;
      }
    }
  }
  return ecg_output;
};

/**
 * Builds a Ricker (Mexican hat) wavelet vector centred on zero for ECG wavelet matching. Derives the amplitude from the width parameter and evaluates the modulated Gaussian over an index sequence produced by createRange.
 *
 * @param point - Number of points in the wavelet vector.
 * @param a - Width parameter of the wavelet.
 * @returns The wavelet samples.
 */
export const Signal_ricker = function (point: any, a: any) {
  // point: Number of points in `vector`. Will be centered around 0.
  // a: Width parameter of the wavelet
  const A = 2 / (Math.sqrt(3 * a) * Math.PI ** 0.25);
  const wsq = a ** 2;
  const arrange_point = createRange(0, parseInt(point, 10));
  const vec = [];
  const xsq = [];
  const mod = [];
  const gauss = [];
  const total = [];
  for (let index = 0; index < arrange_point.length; index += 1) {
    vec.push(arrange_point[index] - (point - 1.0) / 2);
    xsq.push(vec[vec.length - 1] ** 2);
    mod.push(1 - xsq[xsq.length - 1] / wsq);
    gauss.push(Math.exp(-xsq[xsq.length - 1] / (2 * wsq)));
    total.push(A * mod[mod.length - 1] * gauss[gauss.length - 1]);
  }
  return total;
};

/**
 * Normalizes a pair of IIR coefficient arrays so they can be fed to the lfilter recurrence. Zero-pads the shorter of a and b to the common length and divides both by a[0], substituting 1 when a[0] is zero.
 *
 * @param a - Denominator coefficients.
 * @param b - Numerator coefficients.
 * @returns The padded and normalized a and b arrays.
 */
export const Prepare_for_lfilter = function (a: any, b: any) {
  const n = Math.max(a.length, b.length);
  const a_temp = new Array(n);
  const b_temp = new Array(n);
  if (a.length < n) {
    for (let i = 0; i < n; i += 1) {
      if (i < a.length) {
        a_temp[i] = a[i];
      } else {
        a_temp[i] = 0;
      }
      b_temp[i] = b[i];
    }
  } else if (b.length < n) {
    for (let i = 0; i < n; i += 1) {
      if (i < b.length) {
        b_temp[i] = b[i];
      } else {
        b_temp[i] = 0;
      }
      a_temp[i] = a[i];
    }
  }
  let temp;
  if (a[0] !== 0) {
    temp = a[0];
  } else {
    temp = 1;
  }
  for (let i = 0; i < n; i += 1) {
    a_temp[i] /= temp;
    b_temp[i] /= temp;
  }

  // a = a_temp;

  // b = b_temp;
  return { a: a_temp, b: b_temp };
};

/**
 * Computes the steady-state initial delay values of an IIR filter, mirroring the scipy lfilter_zi construction. Builds the identity-minus-companion matrix from a, derives B from b and a, seeds the first delay from the sum of B over the first matrix column and fills the rest by accumulating the a and b sums.
 *
 * @param a - Denominator coefficients.
 * @param b - Numerator coefficients.
 * @returns Initial delay state for the filter.
 */
export const lfilter_zi = function (a: any, b: any) {
  // Prepare_for_lfilter(ref a, ref b);
  // const IminusA = [new Array(a.length - 1).fill(0), new Array(a.length - 1).fill(0)];
  const IminusA = [];
  for (let i = 0; i < a.length - 1; i += 1) {
    IminusA.push(new Array(a.length - 1).fill(0));
  }
  for (let i = 0; i < a.length - 1; i += 1) {
    IminusA[i][i] = 1;
  }
  if (a[0] === 0) {
    a[0] = 1;
  }

  // const linalg = [new Array(a.length - 1), new Array(a.length - 1)];
  const linalg = [];
  for (let i = 0; i < a.length - 1; i += 1) {
    linalg.push(new Array(a.length - 1));
  }

  for (let j = 0; j < a.length - 1; j += 1) {
    for (let i = 0; i < a.length - 1; i += 1) {
      if (j === 0) {
        linalg[i][j] = -a[i + 1] / a[0];
      } else {
        linalg[j - 1][j] = 1;
      }
      IminusA[i][j] -= linalg[i][j];
    }
  }
  const B = new Array(a.length - 1);
  for (let i = 1; i < a.length; i += 1) {
    if (i < b.length) {
      B[i - 1] = b[i] - a[i] * b[0];
    } else {
      B[i - 1] = -(a[i] * b[0]);
    }
  }
  const zi = new Array(a.length - 1).fill(0);
  let Iminus_sum = 0;
  for (let i = 0; i < a.length - 1; i += 1) {
    Iminus_sum += IminusA[i][0];
  }
  zi[0] = _.sum(B) / Iminus_sum;
  let asum = 1.0;
  let csum = 0.0;
  for (let i = 0; i < a.length - 1; i += 1) {
    asum += a[i];
    csum += b[i] - a[i] * b[0];
    zi[i] = asum * zi[0] - csum;
  }
  return zi;
};

/**
 * Applies an IIR filter from its a and b coefficients and an initial delay state, mirroring the scipy lfilter recurrence. Logs and returns null when the coefficient arrays differ in length, otherwise copies zi into the delay line and updates it for every sample.
 *
 * @param x - Samples to filter.
 * @param a - Denominator coefficients.
 * @param b - Numerator coefficients.
 * @param zi - Initial delay state.
 * @returns Filtered samples, or null when the coefficient lengths disagree.
 */
export const lfilter = function (x: any, a: any, b: any, zi: any) {
  if (a.length !== b.length) {
    console.log(
      'Exception at lfilter method: A and B filter coefficients should have the same length',
    );
    return null;
  }
  const y = new Array(x.length);
  const N = a.length;
  const d = new Array(N);
  // zi.CopyTo(d, 0);
  copyArray(zi, 0, d, 0, zi.length);

  for (let n = 0; n < x.length; n += 1) {
    y[n] = b[0] * x[n] + d[0];
    for (let f = 1; f < N; f += 1) {
      if (d[f]) {
        d[f - 1] = b[f] * x[n] - a[f] * y[n] + d[f];
      } else {
        d[f - 1] = b[f] * x[n] - a[f] * y[n];
      }
    }
  }
  return y;
};

/**
 * Drops beat locations whose ECG amplitude falls outside the plausible range. Keeps locations where the amplitude magnitude is under 4 combined with a boolean check against 0.01, returning the original list only when that filter yields a falsy value.
 *
 * @param locs - Candidate beat locations.
 * @param new_ecg - ECG samples the locations index into.
 * @returns The retained beat locations.
 */
const Remove_low_peak = function (locs: any, new_ecg: any) {
  /**
   * Computes the peak locations whose corresponding ECG amplitude is below 4. Derived by filtering locs against the new_ecg amplitude condition.
   *
   * @returns Locations passing the amplitude condition
   */
  const suitable_locs = locs.filter(
    (peak: any) => Math.abs(new_ecg[peak]) < 4 && Math.abs((new_ecg[peak] >= 0.01) as any),
  );
  if (suitable_locs) {
    return suitable_locs;
  }
  return locs;
};

/**
 * Suppresses implausibly large peaks in a Pan-Tompkins peak set. Recomputes the mean from the peaks within ten times the average, then zeroes peaks at or above ten times that mean and clamps those above three times it while keeping their locations, and drops the zeroed entries from the returned amplitudes.
 *
 * @param pks - Peak amplitudes, modified in place.
 * @param locs - Sample locations matching the peaks.
 * @returns The surviving peak amplitudes together with their locations.
 */
export const Remove_High_Sample = function (pks: any, locs: any) {
  const new_locs = [];
  if (pks.length) {
    let Average = getAverage(pks);
    /**
     * Computes the subset of peaks whose absolute amplitude is at most ten times the mean amplitude. Derived by filtering pks against ten times the absolute average.
     *
     * @returns Peaks within ten times the average magnitude
     */
    const under_average_peaks = pks.filter((peak: any) => Math.abs(peak) <= Math.abs(Average) * 10);
    if (under_average_peaks.length) {
      Average = getAverage(under_average_peaks);
    }
    for (let i = 0; i < pks.length; i += 1) {
      if (Math.abs(pks[i]) > Math.abs(Average * 3)) {
        if (Math.abs(pks[i]) >= Math.abs(Average * 10)) {
          pks[i] = 0;
        } else {
          pks[i] = Average * 3;
          new_locs.push(locs[i]);
        }
      } else {
        new_locs.push(locs[i]);
      }
    }
  }
  return {
    pks: pks.filter((x: any) => x !== 0),
    locs: new_locs,
  };
};

/**
 * Removes baseline wander from an ECG trace with two cascaded moving-average passes. Averages over a 0.2 second window into an intermediate series, averages that over a 0.6 second window as the baseline estimate, and returns each sample minus the estimate rounded to four decimals.
 *
 * @param ecg - ECG samples.
 * @param samplingRate - Sampling rate defining both window lengths.
 * @returns Baseline-corrected samples.
 */
const newBaseLineAndEnhancement = (ecg: any, samplingRate: any) => {
  const N = ecg.length;

  const flen1 = Math.floor((samplingRate * 0.2) / 2);
  const flen2 = Math.floor((samplingRate * 0.6) / 2);

  // *: Baseline wander remove
  const b1 = new Array(N);
  const nor = new Array(N);
  const bwr = new Array(N);

  for (let j = 0; j < N; j += 1) {
    const start = Math.max(j - flen1, 0);
    const stop = Math.min(j + flen1, N);
    let mean = 0;

    for (let i = start; i < stop; i += 1) {
      mean += ecg[i];
    }

    b1[j] = mean / (stop - start);
  }

  for (let j = 0; j < N; j += 1) {
    const start = Math.max(j - flen2, 0);
    const stop = Math.min(j + flen2, N);
    let mean = 0;

    for (let i = start; i < stop; i += 1) {
      mean += b1[i];
    }

    nor[j] = mean / (stop - start);
    bwr[j] = parseFloat((ecg[j] - nor[j]).toFixed(4));
  }

  return bwr;
};

/**
 * Estimates the initial signal and noise thresholds of the Pan-Tompkins detector from the first two seconds of an ECG trace. Takes a third of the window maximum and half of its average, recomputing both from the samples whose magnitude stays under three times the overall average whenever any remain.
 *
 * @param ecg - ECG samples to estimate from.
 * @param samplingRate - Sampling rate defining the two-second window.
 * @returns Signal and noise threshold pair.
 */
const Calculate_thr = function (ecg: any, samplingRate: any) {
  const average = Math.abs(getAverage(ecg));
  let thr_sig = (_.max(getRange(ecg, 0, Math.min(2 * samplingRate, ecg.length))) * 1) / 3;
  let thr_noise = (getAverage(getRange(ecg, 0, Math.min(2 * samplingRate, ecg.length))) * 1) / 2;
  /**
   * Computes the subset of ECG samples whose absolute amplitude is below three times the signal average. Derived by filtering the ecg array to discard high-amplitude outliers before threshold estimation.
   *
   * @returns ECG samples within three times the average magnitude
   */
  const filtered_ecg = ecg.filter((x: any) => Math.abs(x) < average * 3);
  if (filtered_ecg && filtered_ecg.length > 0) {
    thr_sig =
      (_.max(getRange(filtered_ecg, 0, Math.min(2 * samplingRate, filtered_ecg.length))) * 1) / 3;
    thr_noise =
      (getAverage(getRange(filtered_ecg, 0, Math.min(2 * samplingRate, filtered_ecg.length))) * 1) /
      2;
  }
  return { thr_sig, thr_noise };
};

/**
 * Smooths a signal with a normalized Hanning window, passing it through unchanged for windows shorter than 3. Rounds an even window length up to odd, mirror-pads both edges by half the window and writes the window-weighted sum for each sample.
 *
 * @param raw - Samples to smooth.
 * @param lenWindow - Hanning window length in samples.
 * @returns The smoothed samples.
 */
const RM_Smooth = (raw: any, lenWindow: any) => {
  let updatedLenWindow = lenWindow;
  if (updatedLenWindow < 3) {
    return raw;
  }
  if (updatedLenWindow % 2 === 0) {
    updatedLenWindow += 1;
  }
  // *: Create hanning window
  const hanning = new Array(updatedLenWindow);
  let SumWindow = 0;
  for (let i = 0; i < hanning.length; i += 1) {
    hanning[i] = 0.5 - 0.5 * Math.cos((2.0 * Math.PI * i) / (updatedLenWindow - 1));
    SumWindow += hanning[i];
  }
  const temp = new Array(raw.length + (updatedLenWindow - 1));
  const edge = (updatedLenWindow - 1) / 2;

  // *: Padding
  for (let i = 0; i < edge; i += 1) {
    temp[i] = raw[edge - i];
    temp[temp.length - 1 - i] = raw[raw.length - 1 - edge + i];
  }
  for (let i = 0; i < raw.length; i += 1) {
    temp[edge + i] = raw[i];
  }
  const out_ = new Array(raw.length);

  for (let i = 0; i < temp.length - (updatedLenWindow - 1); i += 1) {
    let sum = 0;
    for (let j = 0; j < updatedLenWindow; j += 1) {
      sum += (hanning[j] * temp[i + j]) / SumWindow;
    }
    out_[i] = sum;
  }
  return out_;
};

/**
 * Clamps every sample of an array to a lower bound and, when supplied, an upper one. Applies Math.max in place and adds Math.min only while value_max differs from the -10 sentinel.
 *
 * @param array - Samples clamped in place.
 * @param value_min - Lower bound applied to every sample.
 * @param value_max - Upper bound; the -10 default leaves samples unbounded above.
 * @returns The same array with clamped values.
 */
const Clip = (array: any, value_min: any, value_max = -10) => {
  for (let index = 0; index < array.length; index += 1) {
    array[index] = Math.max(value_min, array[index]);
    if (value_max !== -10) {
      array[index] = Math.min(value_max, array[index]);
    }
  }
  return array;
};

/**
 * Normalizes ECG amplitudes against a local envelope so the QRS detection thresholds hold across the whole trace. Repeatedly takes the windowed maximum of the absolute samples and zeroes anything above 5 until the peak drops below it, smooths the envelope with a Hanning window, clips it to a third of its mean, replaces near-zero entries with 1 and divides the samples by it in place.
 *
 * @param ecg_baseline - Baseline-corrected ECG samples, normalized in place.
 * @param window_len - Envelope window length in samples.
 * @returns The in-place normalized samples.
 */
const Normalize = (ecg_baseline: any, window_len: any) => {
  const ecg_normalize = ecg_baseline;
  /**
   * Computes the element-wise absolute value of the normalized ECG array. Derived by mapping Math.abs over ecg_normalize.
   *
   * @returns Absolute values of the normalized ECG samples
   */
  const abs_raw = ecg_normalize.map((x: any) => Math.abs(x));
  const g = new Array(ecg_normalize.length);
  while (true) {
    for (let index = 0; index < abs_raw.length; index += 1) {
      const start_index = Math.max(0, index - parseInt((window_len / 2) as any, 10));
      const stop_index = Math.min(
        abs_raw.length - 1,
        index + parseInt((window_len / 2) as any, 10),
      );
      const max_value = _.max(getRange(abs_raw, start_index, stop_index - start_index));
      g[index] = max_value;
    }
    if ((_.max(abs_raw) as any) < 5) {
      break;
    }
    /**
     * Collects the indices of the absolute-value array whose samples exceed 5. Derived by filtering a lodash range over abs_raw against that threshold.
     *
     * @returns Indices of samples greater than 5
     */
    const list_index = _.range(abs_raw.length).filter((x) => abs_raw[x] > 5);
    for (let index = 0; index < list_index.length; index += 1) {
      abs_raw[list_index[index]] = 0;
    }
  }
  let g_smooth = RM_Smooth(g, window_len);
  const g_mean = Math.max(getAverage(g_smooth) / 3.0, 0.1);
  g_smooth = Clip(g_smooth, g_mean);
  /**
   * Collects the indices of the smoothed envelope that fall below 0.01. Derived by filtering a lodash range over the baseline length against g_smooth.
   *
   * @returns Indices where the smoothed envelope is below 0.01
   */
  const smooth_index = _.range(ecg_baseline.length).filter((x) => g_smooth[x] < 0.01);
  if (smooth_index.length > 0) {
    smooth_index.forEach((index) => {
      g_smooth[index] = 1;
    });
  }
  for (let index = 0; index < ecg_normalize.length; index += 1) {
    ecg_normalize[index] /= g_smooth[index];
  }
  return ecg_normalize;
};

/**
 * Detects the QRS beat marker positions of an ECG strip with a Pan-Tompkins pipeline, returning an empty array for missing data. Scales the counts by gain, removes baseline wander, band-passes and envelope-normalizes the trace, squares it and picks candidate peaks, then walks them with adaptive signal and noise thresholds, an RR-based search-back for missed beats and a T-wave slope rejection, finally pruning low peaks from the accepted locations.
 *
 * @param samplingRate - Sampling rate of the strip.
 * @param gain - Channel gain used to scale the raw counts.
 * @param ecgData - Raw ECG samples of the strip.
 * @returns Sample indices of the detected beats.
 */
export const getBeatMarkersArr = function (samplingRate: any, gain: any, ecgData: any) {
  if (!ecgData || ecgData.length === 0) {
    return [];
  }
  let ecg_m = [];

  let new_ecg = Return_real_value(ecgData, gain);
  new_ecg = newBaseLineAndEnhancement(new_ecg, samplingRate);
  let ecg_h = Butterworth_bandpass(new_ecg);
  ecg_h = Normalize(ecg_h, samplingRate * 0.6);

  ecg_m = Pow_list(ecg_h);
  let locs = Find_peaks(ecg_m, 0.00000000001, samplingRate * 0.22);

  locs = Remove_low_peak(locs, ecg_m);

  const pks = List_from_list(ecg_m, locs);

  let peak_index;
  const qrs_c = [];
  let qrs_i = [];
  const qrs_i_raw = [];
  const qrs_amp_raw = [];
  const noise_c = [];
  const noise_i = [];

  let x;
  let x1 = null;
  let skip = 0;
  let ser_back = 0;
  let y_i;
  let x_i;
  let _i_l;

  let { thr_sig: THR_SIG, thr_noise: THR_NOISE } = Calculate_thr(ecg_m, samplingRate);
  let SIG_LEV = THR_SIG;
  let NOISE_LEV = THR_NOISE;

  let { thr_sig: THR_SIG1, thr_noise: THR_NOISE1 } = Calculate_thr(ecg_h, samplingRate);
  let SIG_LEV1 = THR_SIG1;
  let NOISE_LEV1 = THR_NOISE1;

  let m_selected_RR = 0;
  let meanRR = 0;

  for (peak_index = 0; peak_index < pks.length; peak_index += 1) {
    let ecg_array = null;
    ser_back = 0;
    if (
      locs[peak_index] - Math.round(0.15 * samplingRate) >= 1 &&
      locs[peak_index] <= ecg_h.length - 1
    ) {
      ecg_array = getRange(
        ecg_h,
        locs[peak_index] - Math.round(0.15 * samplingRate),
        Math.round(0.15 * samplingRate),
      );
      const result = Find_max_locate(ecg_array);
      y_i = result.y_i;
      _i_l = result.i_l;
      x_i = result.x_i;
    } else {
      if (peak_index === 0) {
        ecg_array = getRange(ecg_h, 0, locs[peak_index]);
        ser_back = 1;
      } else if (locs[peak_index] >= ecg_h.length) {
        ecg_array = getRange(
          ecg_h,
          locs[peak_index] - Math.round(0.15 * samplingRate),
          ecg_h.length - (locs[peak_index] - Math.round(0.15 * samplingRate)),
        );
      }
      if (ecg_array !== null) {
        const result = Find_max_locate(ecg_array);
        y_i = result.y_i;
        _i_l = result.i_l;
        x_i = result.x_i;
      } else {
        y_i = 0;
        _i_l = [];
        x_i = 0;
      }
    }
    if (qrs_c !== null) {
      x = qrs_c.length;
    } else {
      x = 0;
    }
    if (x >= 9) {
      x1 = getRange(qrs_i, x - 9, 9);
      const diffRR = Diff(x1);
      meanRR = getAverage(diffRR);
      const comp = qrs_i[qrs_i.length - 1] - qrs_i[qrs_i.length - 2];
      if (comp <= 0.92 * meanRR || comp >= 1.16 * meanRR) {
        THR_SIG *= 0.5;
        THR_SIG1 *= 0.5;
      } else {
        m_selected_RR = meanRR;
      }
    }
    let test_m = -3000;
    if (m_selected_RR !== 0) {
      test_m = m_selected_RR;
    } else if (meanRR !== 0 && m_selected_RR === 0) {
      test_m = meanRR;
    } else {
      test_m = 0;
    }
    if (test_m !== 0) {
      if (locs[peak_index] - qrs_i[qrs_i.length - 1] >= Math.round(1.66 * test_m)) {
        const obj = Find_max_locate(
          getRange(
            ecg_m,
            qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate),
            locs[peak_index] -
              Math.round(0.2 * samplingRate) -
              (qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate)),
          ),
        );
        const pks_temp = obj.y_i;
        const _i_3 = obj.i_l;
        let locs_temp = obj.x_i;
        locs_temp = qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate) + locs_temp - 1;
        if (pks_temp < getAverage(qrs_c) * 10) {
          if (pks_temp > 1.75 * THR_NOISE) {
            qrs_c.push(pks_temp);
            qrs_i.push(locs_temp);
          }
          let y_i_t;
          let x_i_t;
          let _i_4;
          let _i_5;
          if (locs_temp <= ecg_h.length) {
            const result = Find_max_locate(
              getRange(
                ecg_h,
                locs_temp - Math.round(0.15 * samplingRate),
                Math.round(0.15 * samplingRate),
              ),
            );
            y_i_t = result.y_i;
            _i_4 = result.i_l;
            x_i_t = result.x_i;
          } else {
            const result = Find_max_locate(
              getRange(
                ecg_h,
                locs_temp - Math.round(0.15 * samplingRate),
                Math.round(0.15 * samplingRate),
              ),
            );
            y_i_t = result.y_i;
            _i_5 = result.i_l;
            x_i_t = result.x_i;
          }
          if (y_i_t > THR_NOISE) {
            qrs_i_raw.push(locs_temp - Math.round(0.15 * samplingRate) + (x_i_t - 1));
            qrs_amp_raw.push(y_i_t);
            SIG_LEV1 = 0.25 * y_i_t + 0.75 * SIG_LEV1;
          }
          SIG_LEV = 0.25 * pks_temp + 0.75 * SIG_LEV;
        }
      }
    }

    if (pks[peak_index] >= THR_SIG) {
      skip = 0;
      if (qrs_c.length >= 3) {
        if (locs[peak_index] - qrs_i[qrs_i.length - 1] <= Math.round(0.36 * samplingRate)) {
          const Slope1_array = Diff(
            getRange(
              ecg_m,
              locs[peak_index] - Math.round(0.075 * samplingRate),
              Math.round(0.075 * samplingRate),
            ),
          );
          const Slope1 = getAverage(Slope1_array);
          const Slope2_array = Diff(
            getRange(
              ecg_m,
              qrs_i[qrs_i.length - 1] - Math.round(0.075 * samplingRate),
              Math.round(0.075 * samplingRate),
            ),
          );
          const Slope2 = getAverage(Slope2_array);
          if (Math.abs(Slope1) <= Math.abs(0.5 * Slope2)) {
            noise_c.push(pks[peak_index]);
            noise_i.push(locs[peak_index]);
            skip = 1;
            NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
            NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
          } else {
            skip = 0;
          }
        }
      }

      if (skip === 0) {
        qrs_c.push(pks[peak_index]);
        qrs_i.push(locs[peak_index]);
        if (y_i >= THR_SIG1) {
          if (ser_back === 1) {
            qrs_i_raw.push(x_i);
          } else {
            qrs_i_raw.push(locs[peak_index] - Math.round(0.15 * samplingRate) + (x_i - 1));
          }
        }
        qrs_amp_raw.push(y_i);
        SIG_LEV1 = 0.125 * y_i + 0.875 * SIG_LEV1;
      }

      SIG_LEV = 0.125 * pks[peak_index] + 0.875 * SIG_LEV;
    } else if (THR_NOISE <= pks[peak_index] && pks[peak_index] < THR_SIG) {
      NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
      NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
    } else if (pks[peak_index] < THR_NOISE) {
      noise_c.push(pks[peak_index]);
      noise_i.push(locs[peak_index]);
      NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
      NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
    }

    if (NOISE_LEV !== 0 || SIG_LEV !== 0) {
      THR_SIG = NOISE_LEV + 0.25 * Math.abs(SIG_LEV - NOISE_LEV);
      THR_NOISE = 0.5 * THR_SIG;
    }
    if (NOISE_LEV1 !== 0 || SIG_LEV1 !== 0) {
      THR_SIG1 = NOISE_LEV1 + 0.25 * Math.abs(SIG_LEV1 - THR_NOISE1);
      THR_NOISE1 = 0.5 * THR_SIG1;
    }
  }
  qrs_i = Remove_low_peak(qrs_i, ecg_m);
  return qrs_i;
};

/**
 * Second-order Butterworth low-pass over an ECG trace, passing the input straight through for a zero cutoff and yielding null when no data is given. Pads the trace with two extra points at each end, derives the a to e coefficients from the tangent of the normalized cutoff, then filters recursively in one direction and back in the other before copying the result out.
 *
 * @param inData - ECG samples to filter.
 * @param SamplingRate - Sampling rate of the trace.
 * @param CutOff - Low-pass cutoff frequency; 0 disables filtering.
 * @returns The filtered samples, the input for a zero cutoff, or null when no data was given.
 */
export const Butterworth = (inData: any, SamplingRate: any, CutOff: any) => {
  if (!inData) {
    return null;
  }

  if (CutOff === 0) {
    return inData;
  }

  const dF2 = inData.length - 1; // The data range is set with dF2
  const Dat2 = new Array(dF2 + 4); // Array with 4 extra points front and back
  const data = new Array(inData.length); // Ptr., changes passed data
  copyArray(inData, 0, data, 0, inData.length);

  copyArray(inData, 0, Dat2, 2, dF2);
  Dat2[0] = inData[0];
  Dat2[1] = Dat2[0];
  Dat2[dF2 + 2] = inData[dF2];
  Dat2[dF2 + 3] = Dat2[dF2 + 2];

  const wc = Math.tan((CutOff * Math.PI) / SamplingRate);
  const k1 = Math.SQRT2 * wc; // Sqrt(2) * wc
  const k2 = wc * wc;
  const a = k2 / (1 + k1 + k2);
  const b = 2 * a;
  const c = a;
  const k3 = b / k2;
  const d = -2 * a + k3;
  const e = 1 - 2 * a - k3;

  // RECURSIVE TRIGGERS - ENABLE filter is performed (first, last points constant)
  const DatYt = new Array(dF2 + 4);
  DatYt[0] = inData[0];
  DatYt[1] = DatYt[0];
  for (let s = 2; s < dF2 + 2; s += 1) {
    DatYt[s] =
      a * Dat2[s] + b * Dat2[s - 1] + c * Dat2[s - 2] + d * DatYt[s - 1] + e * DatYt[s - 2];
  }
  DatYt[dF2 + 2] = DatYt[dF2 + 1];
  DatYt[dF2 + 3] = DatYt[dF2 + 2];

  // FORWARD filter
  const DatZt = new Array(dF2 + 2);
  DatZt[dF2] = DatYt[dF2 + 2];
  DatZt[dF2 + 1] = DatYt[dF2 + 3];
  for (let t = -dF2 + 1; t <= 0; t += 1) {
    DatZt[-t] =
      a * DatYt[-t + 2] +
      b * DatYt[-t + 3] +
      c * DatYt[-t + 4] +
      d * DatZt[-t + 1] +
      e * DatZt[-t + 2];
  }

  copyArray(DatZt, 0, data, 0, dF2);
  return data;
};
